import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { listTaskTemplatesApi, instantiateTaskFromTemplateApi, AgeGroupCode } from '../../services/apiService';
import { i18n } from '../../i18n/i18n';
import { pluralKey } from '../../utils/starGoal';
import { useLanguage } from '../../context/LanguageContext';
import { showToast } from '../kid/Toast';
import TaskDetails from '../TaskDetails';
import { ageLabel, taskLibraryTemplateToPreviewTask } from './adminHelpers';

/** Admin → Tasks → Task library: standard tasks (folded away by default) and the family's saved templates. */
/** A standard chain (2026-10-13): its steps in order; "Use" adds them all, linked. */
const ChainSteps: React.FC<{ chain: Array<{ title: string; wait_hours: number }>; loops: boolean }> = ({ chain, loops }) => (
  <div className="mb-3 rounded-kid-tile bg-kid-green-bg dark:bg-green-950/40 px-3 py-2 text-sm text-kid-text dark:text-kid-text-dark">
    <p className="font-bold mb-1">{i18n.t('library.chain_title').replace('{{n}}', String(chain.length))}</p>
    <ol className="list-decimal pl-5 space-y-0.5">
      {chain.map((step, k) => (
        <li key={k}>
          {step.title}
          {(k < chain.length - 1 || loops) && step.wait_hours > 0 && (
            <span className="text-kid-text-muted dark:text-kid-text-muted-dark"> · {i18n.t(pluralKey('library.chain_wait', step.wait_hours)).replace('{{n}}', String(step.wait_hours))}</span>
          )}
        </li>
      ))}
    </ol>
    {loops && <p className="mt-1 text-kid-text-muted dark:text-kid-text-muted-dark">🔁 {i18n.t('library.chain_loops')}</p>}
  </div>
);

const TaskLibrary: React.FC<{ onTaskAdded?: (taskId: string, title: string) => void }> = ({ onTaskAdded }) => {
  const { current_family, refreshFamilyData } = useAppContext() as any;
  const { current_language } = useLanguage();
  const [ageFilter, setAgeFilter] = useState<AgeGroupCode | 'all'>('all');
  // The standard-task list is long (80+), so it starts folded away; the choice is remembered on this device.
  const [showStandard, setShowStandard] = useState<boolean>(() => {
    try { return window.localStorage.getItem('antnest.admin.showStandardTasks') === '1'; } catch { return false; }
  });
  const toggleStandard = () => setShowStandard((v) => {
    try { window.localStorage.setItem('antnest.admin.showStandardTasks', v ? '0' : '1'); } catch { /* private mode */ }
    return !v;
  });
  const [systemTemplates, setSystemTemplates] = useState<any[]>([]);
  const [familyTemplates, setFamilyTemplates] = useState<any[]>([]);
  const [previewTaskLibraryTemplate, setPreviewTaskLibraryTemplate] = useState<any | null>(null);
  const [useTemplateBusy, setUseTemplateBusy] = useState(false);
  useEffect(() => {
    const load = async () => {
      try {
        const lang = current_language;
        const age = ageFilter === 'all' ? undefined : ageFilter;
        const [sys, fam] = await Promise.all([
          listTaskTemplatesApi(lang, { age_group: age, system_only: true }),
          listTaskTemplatesApi(lang, { age_group: age, family_only: true }),
        ]);
        setSystemTemplates(sys);
        setFamilyTemplates(fam);
      } catch (e) {
        console.error('Failed to load task templates', e);
      }
    };
    load();
  }, [current_language, ageFilter]);

  const ageOptions: Array<{ code: AgeGroupCode | 'all'; label: string }> = (
    ['all', 'age_2_3', 'age_4_5', 'age_6_7', 'age_8_9', 'age_10_12', 'age_13_plus', 'adults'] as Array<AgeGroupCode | 'all'>
  ).map((code) => ({ code, label: ageLabel(code) }));

  const handleUseTemplate = async (tplId: string) => {
    if (useTemplateBusy) return;
    setUseTemplateBusy(true);
    try {
      const created = await instantiateTaskFromTemplateApi(tplId);
      await refreshFamilyData();
      // Take the person to the task they just added (2026-10-13, Jens: staying in the library confused people).
      if (created?.id) onTaskAdded?.(created.id, created.title);
    } catch (e) {
      console.error('Failed to use template', e);
      showToast({ message: (e as Error)?.message || i18n.t('admin_panel.err_template_use'), tone: 'error' });
    } finally {
      setUseTemplateBusy(false);
    }
  };


  return (
    <>
        {previewTaskLibraryTemplate && current_family && (
          <TaskDetails
            task={taskLibraryTemplateToPreviewTask(previewTaskLibraryTemplate, current_family)}
            previewMode
            previewRoomLabel={previewTaskLibraryTemplate.suggested_room_original_name || null}
            onClose={() => setPreviewTaskLibraryTemplate(null)}
            onUpdate={() => {}}
          />
        )}
        {/* Task library: standard (platform) + my templates (family-only) */}
        <div className="mb-8 space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h4 className="font-kid-heading text-xl font-extrabold text-kid-text dark:text-kid-text-dark">{i18n.t('library.title')}</h4>
              <p className="text-sm text-kid-text-muted dark:text-kid-text-muted-dark mt-0.5">
                {i18n.t('library.intro')}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <label className="text-sm text-kid-text dark:text-kid-text-dark">{i18n.t('library.age')}</label>
              <select
                className="px-3 py-2 border-2 border-kid-border-strong dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark rounded-kid-tile focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={ageFilter}
                onChange={(e) => setAgeFilter(e.target.value as AgeGroupCode | 'all')}
              >
                {ageOptions.map(opt => (
                  <option key={opt.code} value={opt.code}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <button
              type="button"
              onClick={toggleStandard}
              aria-expanded={showStandard}
              className="w-full min-h-[56px] flex items-center gap-3 px-4 mb-3 rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark text-left text-kid-text dark:text-kid-text-dark"
            >
              <span className="flex-1 font-bold text-lg">
                {i18n.t('library.standard_title')} ({systemTemplates.length})
              </span>
              <span className="font-bold text-kid-green-shadow dark:text-green-300">
                {showStandard ? i18n.t('library.hide') : i18n.t('library.show')} <span aria-hidden>{showStandard ? '▲' : '▼'}</span>
              </span>
            </button>
            {!showStandard ? null : systemTemplates.length === 0 ? (
              <div className="text-center text-kid-text-muted py-4 text-sm">{i18n.t('library.none_standard')}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {systemTemplates.map((tpl: any) => (
                  <div key={tpl.id} className="bg-dashboard-card dark:bg-dashboard-card-dark rounded-kid-card border-2 border-dashboard-border dark:border-dashboard-border-dark p-6">
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <h5 className="text-lg font-semibold text-kid-text dark:text-kid-text-dark">{tpl.title}</h5>
                      <span className="text-xs px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100 shrink-0">{i18n.t('library.badge_standard')}</span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs px-2 py-1 rounded-full bg-kid-chip dark:bg-kid-chip-dark text-kid-text dark:text-kid-text-dark">{ageLabel(tpl.age_group)}</span>
                    </div>
                    {tpl.description && (
                      <p className="text-sm text-kid-text dark:text-kid-text-dark mb-2 line-clamp-3">{tpl.description}</p>
                    )}
                    {Array.isArray(tpl.chain) && tpl.chain.length > 1 && <ChainSteps chain={tpl.chain} loops={!!tpl.chain_loops} />}
                    <div className="text-sm text-kid-text dark:text-kid-text-dark mb-3 flex gap-2 flex-wrap">
                      {tpl.points !== undefined && <span>⭐ {tpl.points}</span>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50"
                        onClick={() => setPreviewTaskLibraryTemplate(tpl)}
                      >
                        {i18n.t('task_details.view')}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50"
                        disabled={useTemplateBusy}
                        onClick={() => handleUseTemplate(tpl.id)}
                      >
                        {useTemplateBusy ? '…' : i18n.t('library.use')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h5 className="text-lg font-medium text-kid-text dark:text-kid-text-dark mb-2">{i18n.t('library.my_title')}</h5>
            <p className="text-sm text-kid-text-muted dark:text-kid-text-muted-dark mb-3">
              {i18n.t('library.my_intro')}
            </p>
            {familyTemplates.length === 0 ? (
              <div className="text-center text-kid-text-muted py-4 text-sm">{i18n.t('library.none_my')}</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {familyTemplates.map((tpl: any) => (
                  <div key={tpl.id} className="bg-dashboard-card dark:bg-dashboard-card-dark rounded-kid-card border-2 border-dashboard-border dark:border-dashboard-border-dark p-6">
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <h5 className="text-lg font-semibold text-kid-text dark:text-kid-text-dark">{tpl.title}</h5>
                      <span className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-900 dark:text-blue-100 shrink-0">{i18n.t('library.badge_my')}</span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs px-2 py-1 rounded-full bg-kid-chip dark:bg-kid-chip-dark text-kid-text dark:text-kid-text-dark">{ageLabel(tpl.age_group)}</span>
                    </div>
                    {tpl.description && (
                      <p className="text-sm text-kid-text dark:text-kid-text-dark mb-2 line-clamp-3">{tpl.description}</p>
                    )}
                    <div className="text-sm text-kid-text dark:text-kid-text-dark mb-3 flex gap-2 flex-wrap">
                      {tpl.points !== undefined && <span>⭐ {tpl.points}</span>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50"
                        onClick={() => setPreviewTaskLibraryTemplate(tpl)}
                      >
                        {i18n.t('task_details.view')}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50"
                        disabled={useTemplateBusy}
                        onClick={() => handleUseTemplate(tpl.id)}
                      >
                        {useTemplateBusy ? '…' : i18n.t('library.use')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
    </>
  );
};

export default TaskLibrary;
